/**
 * Fitting a screen's panel to the viewport it actually got.
 *
 * ## Why this is not more media queries
 *
 * The start screen has to say four scoring rules, a streak rule, three ways to
 * begin and a title, and it says them well — the tiles already collapse from a
 * four-column grid to two, then to stacked rows, as the screen narrows. What no
 * breakpoint can do is know how much room is left, because that is not a
 * function of width. A phone in portrait is over every `max-height` rule in the
 * stylesheet and under every `max-width` one; Safari's URL bar moves the answer
 * by 90px while the page is open; and the copy is built from `MAX_BUSTS` and
 * `POINTS`, so it changes length when the game is balanced.
 *
 * Measuring costs one layout read and is right on every device, including the
 * ones that did not exist when the breakpoints were written.
 *
 * ## Why the panel is pulled up as well as scaled
 *
 * `transform` changes what is painted and not what is laid out. Scaled alone,
 * the scroll container still believes the panel is full height, so the screen
 * keeps its scrollbar and scrolls over a region that is now empty — the bug
 * traded for a stranger one.
 *
 * So the scale is paired with a negative bottom margin of exactly the height
 * the scale removed. The layout box then matches the painted box, `overflow-y:
 * auto` sees no overflow and takes its scrollbar away by itself, and the fit
 * needs no help from `overflow: hidden` to look right.
 *
 * That margin also decides the alignment, which is why it is applied through an
 * attribute rather than always. `.w21-panel` centres itself with `margin: auto`,
 * and an `auto` margin only centres while it has free space to absorb. Pinning
 * the bottom margin when the panel already fits would hand every spare pixel to
 * the top one and drop the panel to the floor of the screen. Untouched, it
 * centres; pulled, free space is exactly zero and it sits at the top of a box
 * it now fills.
 */

/**
 * How far the panel may be shrunk before scrolling is the better answer.
 *
 * There is a point below which the tile captions stop being something a person
 * reads and start being texture, and a screen that fits but cannot be read is
 * not better than one that scrolls.
 *
 * Set from measurement rather than taste. The tightest viewport worth caring
 * about is 320x480 — a small phone in landscape, or a window dragged to a
 * sliver — and the start screen needs 0.568 there. An earlier 0.62 was above
 * that by just enough to leave 41px hanging off the bottom, which is the worst
 * of both: shrunk *and* scrolling. The floor sits below the hardest real case
 * so that scrolling is only ever reached by something genuinely absurd.
 */
export const MIN_FIT = 0.55;

/**
 * Scale that brings `natural` into `available`, never above 1.
 *
 * Kept separate from the DOM so the arithmetic — and the floor, and every
 * degenerate measurement a detached or hidden element produces — can be tested
 * without a browser.
 */
export function fitScale(natural: number, available: number, min = MIN_FIT): number {
  if (!Number.isFinite(natural) || !Number.isFinite(available)) return 1;
  if (natural <= 0 || available <= 0) return 1;
  if (natural <= available) return 1;
  return Math.max(min, available / natural);
}

/**
 * Layout height to take back off the panel, given the space there is.
 *
 * Deliberately *not* `panel * (1 - scale)`, which is the height the scale
 * removed from the panel alone. What has to end up occupying `available` is
 * everything the fit shrank, decorations included, and the panel is merely the
 * only box in that set with layout height to give. Taking back the panel's own
 * share would leave the difference as free space, and — since the top margin
 * stays `auto` on a panel that is centring itself — the browser would hand that
 * free space straight back to the top margin and push the panel down by exactly
 * the amount that was just saved.
 *
 * Pinning the panel's margin box to `available` has neither problem, and needs
 * no knowledge of what the decorations cost.
 */
export function fitPull(panel: number, available: number): number {
  if (!Number.isFinite(panel) || !Number.isFinite(available)) return 0;
  return Math.max(0, panel - available);
}

/**
 * Keeps one screen's panel fitted to the space the screen has.
 *
 * Driven by `ResizeObserver` rather than a `resize` listener, because the two
 * things that change the answer are the screen getting shorter (a rotation, a
 * browser bar sliding away) and the panel getting taller (a status line
 * appearing under the buttons, a webfont arriving and reflowing the copy). One
 * observer on each catches both, and neither is a window event.
 *
 * Applying a fit cannot re-trigger the observer: a transform does not change a
 * border box, and a margin is not part of one.
 */
export class PanelFit {
  readonly #screen: HTMLElement;
  readonly #panel: HTMLElement;

  #observer: ResizeObserver | null = null;
  #applied = 1;

  /**
   * @param scrolls - when true this measures nothing and scales nothing: the
   *   screen is allowed to be taller than the viewport and to scroll. For a
   *   menu that is the right answer and shrinking is the wrong one — scaling a
   *   panel to fit its height shrinks its *width* by the same factor, so a tall
   *   menu ends up as a narrow column down the middle of a wide screen with
   *   empty space either side. A player scrolls a menu without being taught;
   *   nobody reads a column at two thirds size and thinks it is deliberate.
   *
   *   The play screens keep the fit, because a board that scrolls is a board
   *   with cards you cannot see.
   */
  constructor(screen: HTMLElement, panel: HTMLElement, private readonly scrolls = false) {
    this.#screen = screen;
    this.#panel = panel;
  }

  /** The scale currently applied. 1 when the panel fits unaided. */
  get scale(): number {
    return this.#applied;
  }

  /**
   * Measure and apply.
   *
   * ## Why the screen is measured and not the panel
   *
   * The obvious measurement — the panel's own `offsetHeight` — is wrong, and
   * wrong by about 25px on every device. `.w21-panel::before` is the soft
   * radial scrim behind the words, and it is deliberately bigger than the panel
   * (`inset: -6% -4%`) so the glow surrounds the text instead of stopping at an
   * edge. An absolutely positioned box still counts toward its containing
   * block's scrollable overflow, so that bleed is real scroll: fit the panel
   * exactly and the screen keeps a scrollbar for a couple of dozen pixels of
   * invisible gradient. Confirmed rather than deduced — disabling the pseudo
   * takes `scrollHeight` to exactly `clientHeight`.
   *
   * So the thing measured is what the scroll container itself believes it has,
   * which includes every decoration, in the units the scrollbar is decided in.
   * The panel is still what gets *pulled*, because it is the only thing here
   * with a layout box to take height out of.
   *
   * ## Why the fit is cleared before measuring
   *
   * `scrollHeight` is post-transform, so measuring it while a fit is applied
   * would feed the fit its own output. Clearing first costs one forced layout
   * on a resize or a rotation — never per frame — and the intermediate state is
   * never painted, because nothing yields between here and the re-apply.
   *
   * Only the block-end bleed matters: content above a scroller's start is not
   * reachable and `scrollHeight` does not count it, which is also why the panel
   * being pinned to the top by the pull is the arrangement that works.
   */
  measure(): void {
    // A scrolling screen has nothing to fit. Clearing rather than returning
    // early, so a screen that is switched into this mode drops any scale it was
    // already wearing.
    if (this.scrolls) {
      this.#clear();
      this.#applied = 1;
      return;
    }

    const style = getComputedStyle(this.#screen);
    const padding =
      (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    const available = this.#screen.clientHeight - padding;

    this.#clear();
    // Reading `scrollHeight` is what forces the unfitted layout above to happen.
    const natural = this.#screen.scrollHeight - padding;
    const panel = this.#panel.offsetHeight;

    const scale = fitScale(natural, available);
    this.#applied = scale;
    if (scale >= 1) return;

    this.#screen.style.setProperty('--w21-fit', scale.toFixed(4));
    this.#screen.style.setProperty('--w21-fit-pull', `${fitPull(panel, available).toFixed(2)}px`);
    this.#screen.dataset.fitted = 'true';
  }

  /** Back to the unfitted state, which is also how the panel is measured. */
  #clear(): void {
    this.#screen.style.removeProperty('--w21-fit');
    this.#screen.style.removeProperty('--w21-fit-pull');
    delete this.#screen.dataset.fitted;
  }

  /** Begin watching. Safe to call twice. */
  start(): void {
    this.measure();
    if (this.#observer || typeof ResizeObserver === 'undefined') return;
    this.#observer = new ResizeObserver(() => this.measure());
    this.#observer.observe(this.#screen);
    this.#observer.observe(this.#panel);

    // A webfont landing after the first measure changes every line length on
    // the screen at once. Nothing else notices, because the border boxes the
    // observer watches are driven by the copy inside them.
    void globalThis.document?.fonts?.ready.then(() => {
      if (this.#observer) this.measure();
    });
  }

  /** Stop watching, and leave the panel as it was last fitted. */
  stop(): void {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}
